import { useMediaQuery } from "@base-ui/react/unstable-use-media-query";
import { CircleHelpIcon, MenuIcon, SearchIcon, SettingsIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import riksuMark from "../assets/riksu-logo.png";
import { useT } from "../shell/i18n";
import { Account } from "../ui/account";
import type { Photograph } from "../ui/avatar";
import { Action, IconAction, ON_SHELL_CONTROL } from "../ui/button";
import { CreateMenu } from "../ui/create-menu";
import { ModuleNav, useModuleNav } from "../ui/module-nav";
import { Rail, SearchButton } from "../ui/rail";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import type { Creation } from "./creations";
import { type Destination, narrowedFor, type RailGroup } from "./destinations";
import type { SignInState } from "./session";
import {
  ChatUtilityAction,
  CompactUtilityEntry,
  IconRail,
  Navigation,
  UTILITY,
  UtilityEntry,
} from "./shell-navigation";
import { ChatDock, useShellActions } from "./shell-overlays";

/**
 * THE GRID, and everything a reader looks at while nothing is open over it.
 *
 * Every prop here comes from `Shell` and every one of them is constant for the session, so
 * this component re-renders for exactly two reasons: the signed-in person's photograph
 * arriving, and `useModuleNav()`'s value moving — which is the module, the page address or
 * the sidebar's collapse state. A pane is none of those.
 */
export function Frame({
  state,
  groups,
  who,
  photograph,
  creations,
  children,
}: {
  readonly state: Extract<SignInState, { status: "signed_in" }>;
  readonly groups: readonly RailGroup[];
  readonly who: string;
  /**
   * The reader's own face, as the ANSWER rather than as a URL.
   *
   * This was `string | null` — the credential, and nothing else — which was all the topbar
   * avatar needed. `/` needs the whole answer, because the portrait there has to tell
   * "nobody filed one" from "riksu holds one it cannot serve", and a null URL collapses the
   * two. Widening the existing prop rather than adding a second one: two props for one fact
   * is how the two come to disagree, and it keeps this component's re-render sources at the
   * exact two `shell-render.test.tsx` measures.
   */
  readonly photograph: Photograph;
  readonly creations: readonly Creation[];
  readonly children: ReactNode;
}) {
  const t = useT();
  const [navigationOpen, setNavigationOpen] = useState(false);
  // ONE UTILITY NAVIGATION IN THE ACCESSIBILITY TREE. CSS hiding made the compact
  // topbar links and the permanent rail coexist in the DOM, so a screen reader (and
  // jsdom, which does not apply layout CSS) could encounter two "Inbox" links. This
  // breakpoint decides which renderer exists instead of asking presentation to erase one.
  const wideUtility = useMediaQuery("(min-width: 80rem)", {
    defaultMatches: false,
    noSsr: true,
  });
  // THE PAGE ADDRESS, out of the context that already holds it rather than out of the
  // router. `module-nav.tsx` publishes `at` for the sidebar, the section highlight, the
  // breadcrumb and `RecordLink`; the router below is the fifth reader and reading it here
  // is what keeps this component off the router's own subscription — see `Located`.
  const { at: here } = useModuleNav();
  const { search, create, settings, personalize, help, chat } = useShellActions();
  const aiEnabled = state.riksu.modules.includes("ai");

  // THE SCROLL GOES BACK TO THE TOP WHEN THE PAGE CHANGES, and it needs saying because
  // it was free before and is not any more.
  //
  // While the DOCUMENT scrolled, a navigation replaced the document's scroll position by
  // construction. Now `main` is the scroller and `main` never unmounts, so without this
  // it keeps its `scrollTop` across every navigation: leave /people halfway down, open
  // /leave, and you arrive in the middle of a screen you have never seen. Nothing in
  // `src/` calls `scrollTo` or `scrollIntoView` today, so there was no existing mechanism
  // to extend.
  //
  // KEYED ON `here`, the durable page pathname, and not on the full location. A quick-view
  // press changes history state while leaving `here` alone; keying this effect on that state
  // would scroll the list under the pane back to the top while somebody reads the record
  // they pressed on row forty.
  const content = useRef<HTMLElement>(null);
  const utilities = useMemo(
    () => UTILITY.map((destination) => narrowedFor(destination, state.riksu)),
    [state.riksu],
  );
  // THE DEPENDENCY IS THE TRIGGER, not an input. Biome reads `here` as one dependency
  // more than necessary and is literally right — the body never reads it — but this
  // effect has no value to recompute. Taking the fix leaves an empty array, which
  // scrolls to the top once on mount and is the same as not doing it at all.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `here` is the trigger
  useEffect(() => {
    // `scrollTop = 0` AND NOT `scrollTo({ top: 0 })`, which is not a style preference.
    //
    // jsdom implements `window.scrollTo` and does NOT implement `Element.prototype
    // .scrollTo`, so the call throws `content.current?.scrollTo is not a function`. The
    // throw happens during an effect, React Router's boundary catches it, and the SHELL
    // renders its error page — so the symptom is not "the scroll did not reset", it is
    // every screen in the product rendering nothing. Measured: 634 green became 37 red
    // across 5 files, and the suite caught it on the first run.
    //
    // `scrollTop` is a plain property jsdom accepts and assigns, and it is what every
    // browser does with the object form anyway when there is nothing to smooth.
    if (content.current) {
      content.current.scrollTop = 0;
    }
  }, [here]);

  // `at={here}` ON BOTH NAVIGATIONS. A quick view over `/payroll` has not moved the page,
  // so the rail's administration entry stays lit and the sheet's group stays expanded.
  const rail = (
    <Rail
      tenant={state.riksu.tenant_id}
      who={who}
      companyLabel={t("session.company")}
      oneTenantNote={t("shell.oneTenant")}
      signOutLabel={t("app.signOut")}
      search={
        <SearchButton label={t("palette.open")} shortcut={t("palette.shortcut")} onOpen={search} />
      }
      nav={<Navigation groups={groups} utilities={utilities} at={here} />}
    />
  );

  return (
    // The palette and the toaster live OUTSIDE this — see `Overlays`. Sonner renders a
    // real element in place rather than a portal, so putting it inside made it the first
    // grid child: the rail slid into the second column and the content into a third that
    // the template does not define. The screen came out with a 1184px sidebar and a 256px
    // page, which is a layout bug that looks like a styling one.
    //
    // THE SHELL: a topbar row over three columns, laid out by NAMED AREAS.
    //
    // It was two columns and no bar — a 256px text sidebar beside the content, with a
    // header that existed only below `lg` and was white. Three things the standard
    // asks for did not exist at all: a permanent dark bar, a narrow icon rail, and a
    // canvas that is not the same colour as the panels on it.
    //
    // The file used to argue against the bar, and the argument was sound about the
    // bar it was arguing against: "a bar that repeats the sidebar costs 56 pixels of
    // every screen to say nothing new". This one repeats nothing. It carries the
    // product's single `h1`, the module tabs — which had no home at all, and whose
    // component had zero call sites — and the tenant, the search and the account,
    // which is what lets the rail narrow to icons.
    //
    // Named areas rather than a bare column list: `grid-area` on each region means a
    // region can be reordered or reserved without every sibling's index moving.
    <div
      // `h-dvh` AND NOT `min-h-dvh`, which is the whole of this change.
      //
      // The product owner: "inbox'ta viewport'u aşıyor görüntü aşağı kadar gidiyor
      // onu düzeltelim bu genel kural ya her yerde böyle olmalı." Every screen grew
      // the DOCUMENT, so the topbar and the rails scrolled away and a long table had
      // no bottom.
      //
      // A minimum height is a floor with no ceiling, so `minmax(0, 1fr)` on the
      // content row had nothing to divide: the row grew to its content and `main`'s
      // `flex-1` grew with it. An exact height gives the track something to bound,
      // and everything below is ALREADY CORRECT — the identical flex chain in
      // `ui/rail.tsx` scrolls today inside a `SheetContent` that is `fixed inset-y-0
      // h-full`. Only the ancestor was wrong.
      //
      // The `gridTemplate` below is UNCHANGED and must stay that way. `minmax(0, 1fr)`
      // is what zeroes the content track's automatic minimum size; tidying it to a
      // bare `1fr` reintroduces the overflow while `h-dvh` still looks right, which
      // is the version of this bug that survives review.
      className="grid h-dvh bg-surface-page-backdrop"
      style={{
        // FOUR COLUMNS NOW. `module` is the sidebar of whichever module is open;
        // its track is `auto`, so a module that declares no inner screens — or one
        // whose sidebar somebody has collapsed — renders nothing there and the track
        // measures zero. A collapsed region that still occupies a track is a hairline
        // nobody can explain.
        gridTemplate: `"topbar topbar topbar topbar topbar" var(--size-topbar)
                         "nav module main chat utility" minmax(0, 1fr)
                         / auto auto minmax(0, 1fr) auto auto`,
      }}
    >
      {/* THE FIRST FOCUSABLE THING ON THE PAGE, and it did not exist. Somebody on a
            keyboard arrived at the top of the rail and tabbed through every destination
            before reaching the content, on every navigation. Visible only when focused:
            it is an escape hatch, not furniture. */}
      <a
        href="#content"
        className="sr-only z-(--layer-modal) rounded-md bg-surface-action px-3 py-2 text-shell-label text-on-action focus-visible:not-sr-only focus-visible:absolute focus-visible:top-2 focus-visible:left-2"
      >
        {t("nav.skipToContent")}
      </a>

      <header
        // WHAT THE FOCUS RING KEYS ON. `app.css` re-points the ring inside a dark
        // region, and a marker on the CONTAINER is what lets it: the alternative is
        // every control in here opting into a colour, which is a colour half of
        // them would miss.
        data-shell=""
        style={{ gridArea: "topbar" }}
        // `relative`, WHERE THIS WAS `sticky top-0`. The stickiness never had
        // anywhere to travel: a grid item's containing block is its own area, row one
        // is exactly `var(--size-topbar)`, and the header stretches to fill it. It
        // looked load-bearing and was decorative.
        //
        // `relative` rather than nothing, because `z-index` is IGNORED on a static
        // box — `sticky` was quietly the thing creating the stacking context this
        // `z-(--layer-shell)` depends on, and dropping to static would put the topbar
        // behind the content it is supposed to sit over.
        className="relative z-(--layer-shell) flex items-center gap-3 bg-surface-shell px-3 text-on-shell"
      >
        {/* On a narrow screen the rail is not rendered and the same navigation opens
              in a sheet. A rail reflowed into a row of eight links across a phone is a
              wall, not navigation. */}
        <Sheet open={navigationOpen} onOpenChange={setNavigationOpen}>
          <SheetTrigger
            render={<Action purpose="icon" className={`lg:hidden ${ON_SHELL_CONTROL}`} />}
            aria-label={t("nav.label")}
          >
            <MenuIcon />
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 px-3 py-4"
            onClickCapture={(event) => {
              if (event.target instanceof Element && event.target.closest("a[href]")) {
                setNavigationOpen(false);
              }
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t("nav.label")}</SheetTitle>
            </SheetHeader>
            {rail}
          </SheetContent>
        </Sheet>

        {/* THE DOCUMENT'S ONE `h1`. There was none: `page.tsx` asserted in a docstring
              that its `h2` sat under the shell's `h1`, and that `h1` had never existed —
              so every screen's outline started at level two under nothing. */}
        <h1 className="m-0 flex shrink-0 items-center">
          <img
            src={riksuMark}
            alt=""
            aria-hidden="true"
            className="size-7 object-contain brightness-0 invert"
          />
          <span className="sr-only">{t("app.name")}</span>
        </h1>

        {/* THE RIGHT END: search, then who you are.
         *
         * There was a BELL between them, going to the same screen the utility rail's
         * bell goes to. Two controls for one destination is two things to learn about
         * one thing, and somebody has to press both to find out they agree. The rail
         * keeps it: that rail exists for the surfaces which ACCOMPANY the work rather
         * than replace it, and a notification list is exactly that.
         *
         * `gap-1.5` rather than `gap-1`, now that these controls are genuinely 32px
         * wide. At the 18px they measured, they were nearly touching and the tighter
         * gap was covering for it. */}
        <div className="ml-auto flex items-center gap-1.5">
          {/* THE `+`, before the search, which is where every product that has this
                control puts it: creating is the thing you came to do, finding is how you
                get to it. It renders NOTHING when this caller can create nothing — see
                `ui/create-menu.tsx` for why that is not a disabled button.

                The whole list is `app/creations.ts`. Adding "new expense claim" is a
                line there and no change here, which is the entire arrangement. */}
          <CreateMenu
            label={t("create.open")}
            className={ON_SHELL_CONTROL}
            choices={creations.map((creation) => ({
              id: creation.what,
              label: t(creation.what),
              icon: creation.icon,
            }))}
            onChoose={create}
          />

          <IconAction label={t("palette.open")} className={ON_SHELL_CONTROL} onClick={search}>
            <SearchIcon className="size-4" aria-hidden="true" />
          </IconAction>

          {wideUtility ? null : (
            <nav aria-label={t("nav.utility")} className="contents">
              {utilities.map((item) => (
                <CompactUtilityEntry key={item.to} item={item} at={here} />
              ))}
              {aiEnabled ? <ChatUtilityAction onClick={chat} compact /> : null}
            </nav>
          )}

          {/* THE ACCOUNT, and the sign-out inside it. Narrowing the rail to icons moved
                the tenant and the search up here and DROPPED the sign-out entirely — it
                had lived at the bottom of the wide rail and the icon rail has no room for
                a labelled button, so it simply went. Nobody could leave the product. */}
          {/* THE PERSON RIKSU RESOLVED, and the principal only when it resolved
                  none. This passed `subject` alone, which is the mitra `sub` claim —
                  `dev-admin` on the development stack, an opaque identifier in
                  production — so the one place in the product that says who you are said
                  a login instead of a name, while `GET /v1/session` was already carrying
                  `person.display_name` beside it.

                  Finding nothing stays an ORDINARY answer rather than a fault: a support
                  engineer, a partner administrator and an integration principal all have
                  a login and no employment record. So the principal is the fallback and
                  the menu still works for them — what they do not get is the link to a
                  record they do not have. */}
          <Account
            who={state.riksu.person?.display_name ?? state.riksu.subject ?? t("account.unknown")}
            tenant={state.riksu.tenant_id}
            personId={state.riksu.person?.person_id}
            photograph={photograph.status === "held" ? photograph.url : null}
            onSettings={settings}
            onPersonalize={personalize}
          />
        </div>
      </header>

      {/* THE RAIL: icons, at `--size-nav-rail`. A label here makes it a sidebar, which
            is the shape this replaces — and the standard names the wide text menu as an
            anti-pattern by name. Every icon carries an accessible name and a tooltip,
            because an icon without one is a control nobody can identify. */}
      <aside
        aria-label={t("nav.label")}
        style={{ gridArea: "nav" }}
        // `relative` for the reason `main` carries it — see the note there. A rail
        // scrolls when the window is short enough, and a scroll container that is not
        // a containing block hands its out-of-flow children to the viewport.
        className="relative hidden w-(--size-nav-rail) flex-col overflow-x-hidden overflow-y-auto border-r border-line-subtle bg-surface-panel py-3 lg:flex"
      >
        <IconRail groups={groups} at={here} />

        {/* THE FOOT OF THE RAIL. Help and product settings stay away from destinations:
              they are things you do to the shell, not places in the company's records.
              Both raise a narrow pane without changing the place underneath. */}
        <div className="mt-auto flex flex-col items-center gap-1 border-t border-line-subtle pt-2">
          <IconAction label={t("nav.settings")} onClick={settings}>
            <SettingsIcon className="size-4" aria-hidden="true" />
          </IconAction>
          <IconAction label={t("nav.help")} onClick={help}>
            <CircleHelpIcon className="size-4" aria-hidden="true" />
          </IconAction>
        </div>
      </aside>

      {/* THE UTILITY RAIL, and what it is FOR.

            Reserved and empty until now, on the reasoning that riksu has no secondary
            integrations to put in a tray. That was right about integrations and wrong
            about the rail: what belongs on the right of a data-dense product is the
            surfaces that ACCOMPANY the work rather than replace it — what is waiting on
            you, and what you were just looking at.

            So it holds the two the product already has. A leaf navigates; a parent such as
            Inbox reveals its Tasks, Messages and Notices in the shared module sidebar.
            The narrow sheet declares the same hierarchy rather than dropping this rail. */}
      {wideUtility ? (
        <aside
          style={{ gridArea: "utility" }}
          aria-label={t("nav.utility")}
          className="relative flex w-(--size-utility-rail) flex-col items-center gap-1 overflow-x-hidden overflow-y-auto border-l border-line-subtle bg-surface-panel py-3"
        >
          {utilities.map((item: Destination) => (
            <UtilityEntry key={item.to} item={item} at={here} />
          ))}
          {aiEnabled ? <ChatUtilityAction onClick={chat} /> : null}
        </aside>
      ) : null}

      {/* THE SECOND SIDEBAR, between the icon rail and the content, reading what
            the destination in view declares. See `ui/module-nav.tsx` for why it is a
            sidebar rather than a wider tab strip, and why the state lives in a context
            rather than beside the button that toggles it. */}
      <ModuleNav />

      {/* THE ONE SCROLL CONTAINER IN THE PRODUCT, and it must land with the `h-dvh`
              above rather than after it — the two are one change and either alone is a
              worse product than neither.

              `flex-1` is GONE because it was inert: `main` is a grid ITEM, and flex
              properties do not apply to grid items. It read like the thing doing the work
              and did nothing; the grid row was always what sized this.

              `overflow-auto` and not `overflow-y-auto`: `overflow-y: auto` beside an
              `overflow-x: visible` computes x to `auto` anyway, so the shorter spelling
              is the one that is TRUE. `ui/data-table.tsx` already writes that rule down.

              `outline-none` is GONE and that is an accessibility fix, not tidying. It
              lives in Tailwind's utilities layer and beats `styles/app.css`'s global
              `:focus-visible { outline: … }` — harmless while this was `tabIndex={-1}`
              and a WCAG 2.4.7 failure the moment it became a tab stop with no ring.

              `relative` IS THE FIX FOR THE PAGE-LEVEL SCROLLBAR, and it is not decoration
              beside the `overflow-auto` — it is the half that was missing.

              A scroll container clips its IN-FLOW descendants. It does not clip an
              out-of-flow one whose containing block is somewhere else, and while this was
              `position: static` the containing block for every absolutely positioned
              thing inside it was the INITIAL one — the viewport. Tailwind's `sr-only` is
              `position: absolute`, so every visually-hidden element in the product —
              `ui/announce.tsx`'s live region, `ui/data-table.tsx`'s `<caption>` — was laid
              out at its static position deep inside the scrolled content, against a
              containing block this element could not clip. Each one extended the
              DOCUMENT's scrollable overflow to wherever it happened to sit.

              Measured on `/leave/setup` at 1440x900, before: `body` exactly 900px,
              `body.scrollHeight` 900, nothing outside `main` reaching past the fold — and
              `documentElement.scrollHeight` 1445, `window.scrollTo(0, 400)` moving the
              whole shell up by 400. The deepest escapee was a `div.sr-only` at y=1444,
              which is the 1445 exactly. Setting this one property took the document to
              900 and the page scrollbar with it.

              That is why the symptom is a band of DEAD SPACE below the last card rather
              than more content: what was hanging off the bottom of the document was five
              invisible elements. A screenshot cannot show the cause, which is most of why
              this survived the `h-dvh` change that was supposed to have fixed it.

              THE RULE, for every region in this product that scrolls: a container that
              clips must also be the containing block for what it clips. `min-h-0` lets it
              shrink, `overflow` lets it scroll, and `relative` makes what it holds its
              own. Two of the three were already here. */}
      <main
        ref={content}
        id="content"
        // The product's one scroll container, so SC 2.1.1 requires it be reachable by
        // keyboard. Same exemption and same reason as `app/org-canvas.tsx`.
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region is a widget
        tabIndex={0}
        style={{ gridArea: "main" }}
        className="relative flex w-full min-w-0 min-h-0 flex-col overflow-auto"
      >
        {children}
      </main>
      <ChatDock />
    </div>
  );
}
